import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <div className="border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">L</span>
            </div>
            <span className="text-xl font-bold text-white">
              Loca<span className="text-emerald-400">Leap</span>
            </span>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <FileText className="w-10 h-10 text-emerald-400" />
          <h1 className="text-4xl font-bold text-white">Terms of Service</h1>
        </div>

        <p className="text-slate-400 mb-8">
          Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>

        <div className="prose prose-invert max-w-none">
          <div className="space-y-8 text-slate-300">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
              <p className="leading-relaxed">
                By accessing or using LocaLeap ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service. The Service is provided by VSW Agency / Merge Publishing ("we," "us," or "our").
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Description of Service</h2>
              <p className="leading-relaxed">
                LocaLeap is an AI-powered brand strategy tool that generates reports based on information you provide. The Service analyzes your business information, competitor data, and publicly available information to create brand strategy recommendations.
              </p>
            </section>

            <section className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
              <h2 className="text-2xl font-semibold text-yellow-400 mb-4">3. AI-Generated Content Disclaimer</h2>
              <p className="leading-relaxed text-yellow-100">
                <strong>IMPORTANT:</strong> The brand strategies, recommendations, and insights provided by LocaLeap are generated by artificial intelligence technology. While we strive to provide useful and relevant information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-yellow-100/80 mt-4">
                <li><strong>We do not guarantee the accuracy, completeness, or reliability</strong> of any AI-generated content.</li>
                <li>The content should be used as a <strong>starting point for discussion</strong> and not as definitive business advice.</li>
                <li><strong>We are not responsible for any business decisions</strong> made based on the generated reports.</li>
                <li>You should <strong>verify all information independently</strong> before implementing any strategies.</li>
                <li>Results may vary and <strong>success is not guaranteed</strong>.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Intellectual Property</h2>
              <p className="leading-relaxed mb-4">
                <strong>Our Property:</strong> The LocaLeap platform, including its design, features, technology, branding, and underlying code, is the exclusive property of VSW Agency / Merge Publishing and is protected by intellectual property laws.
              </p>
              <p className="leading-relaxed mb-4">
                <strong>Your Content:</strong> You retain ownership of the business information you provide. However, by using the Service, you grant us a limited license to use this information to generate your reports and improve our services.
              </p>
              <p className="leading-relaxed">
                <strong>Generated Reports:</strong> The reports generated for you are for your personal business use only. You may not resell, redistribute, or commercialize the reports or use them to create a competing service.
              </p>
            </section>

            <section className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
              <h2 className="text-2xl font-semibold text-red-400 mb-4">5. Prohibited Uses</h2>
              <p className="leading-relaxed text-red-100 mb-4">You agree NOT to:</p>
              <ul className="list-disc pl-6 space-y-2 text-red-100/80">
                <li><strong>Copy, replicate, or reverse engineer</strong> any part of the LocaLeap service or technology</li>
                <li><strong>Resell or redistribute</strong> generated reports to third parties for commercial purposes</li>
                <li><strong>Use the Service to create a competing product</strong> or service</li>
                <li><strong>Scrape, harvest, or collect data</strong> from our platform using automated means</li>
                <li><strong>Submit false or misleading information</strong></li>
                <li><strong>Use the Service for any illegal purpose</strong></li>
                <li><strong>Attempt to gain unauthorized access</strong> to our systems or other users' data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. User Responsibilities</h2>
              <p className="leading-relaxed mb-4">You are responsible for:</p>
              <ul className="list-disc pl-6 space-y-2 text-slate-400">
                <li>Providing accurate and truthful information about your business</li>
                <li>Maintaining the confidentiality of your account information</li>
                <li>All activities that occur under your account</li>
                <li>Ensuring your use of the Service complies with all applicable laws</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Limitation of Liability</h2>
              <p className="leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, VSW AGENCY / MERGE PUBLISHING SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-400 mt-4">
                <li>Your use or inability to use the Service</li>
                <li>Any AI-generated content or recommendations</li>
                <li>Business decisions made based on our reports</li>
                <li>Any unauthorized access to or use of our servers</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Beta Service</h2>
              <p className="leading-relaxed">
                LocaLeap is currently in beta and offered free of charge for a limited time. We reserve the right to modify, suspend, or discontinue the Service at any time without notice. Beta features may be unstable and are provided "as is."
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Modifications to Terms</h2>
              <p className="leading-relaxed">
                We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting. Your continued use of the Service after changes constitutes acceptance of the modified terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Governing Law</h2>
              <p className="leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law principles.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Contact Information</h2>
              <p className="leading-relaxed">
                For questions about these Terms of Service, please contact us:
              </p>
              <div className="mt-4 p-4 bg-slate-900/50 border border-slate-800 rounded-xl">
                <p className="text-emerald-400 font-semibold">VSW Agency / Merge Publishing</p>
                <p className="text-slate-400">Email: <a href="mailto:don@vswagency.com" className="text-emerald-400 hover:underline">don@vswagency.com</a></p>
                <p className="text-slate-400">Website: <a href="http://vswagency.com" className="text-emerald-400 hover:underline" target="_blank" rel="noopener noreferrer">vswagency.com</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Simple Footer */}
      <div className="border-t border-slate-800 py-8 px-4">
        <div className="max-w-4xl mx-auto text-center text-slate-500 text-sm">
          © {new Date().getFullYear()} LocaLeap by VSW Agency / Merge Publishing. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
